import { useState } from "react";
import { ShieldAlert, X } from "lucide-react";

/**
 * Trust guardrail shown across the app: HospiceGuard is advisory and never makes
 * autonomous clinical / billing / eligibility decisions.
 *  - variant="global": thin, dismissible-per-session bar (used in the app shell)
 *  - variant="inline": a compact notice for AI-output surfaces (default)
 */
export const ClinicalReviewBanner = ({ variant = "inline", className = "" }) => {
  const [dismissed, setDismissed] = useState(() => {
    if (variant !== "global" || typeof window === "undefined") return false;
    return sessionStorage.getItem("__hg_clinical_review_dismissed") === "1";
  });

  if (variant === "global") {
    if (dismissed) return null;
    return (
      <div
        data-testid="clinical-review-global-banner"
        className="w-full bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200/80 dark:border-amber-900/50 text-amber-900 dark:text-amber-200"
      >
        <div className="max-w-7xl mx-auto flex items-center gap-2 px-4 py-1.5 text-xs">
          <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="flex-1 leading-snug">
            <strong className="font-semibold">Clinical review required.</strong>{" "}
            HospiceGuard provides advisory guidance and does not make autonomous clinical,
            billing, or eligibility decisions — a qualified professional must review every
            recommendation.
          </span>
          <button
            type="button"
            onClick={() => {
              setDismissed(true);
              try {
                sessionStorage.setItem("__hg_clinical_review_dismissed", "1");
              } catch (e) {
                /* sessionStorage disabled — degrade silently */
              }
            }}
            className="p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
            aria-label="Dismiss clinical review notice"
            data-testid="clinical-review-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="clinical-review-inline-banner"
      className={
        "flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-900/50 " +
        "bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 " +
        className
      }
    >
      <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <span className="leading-snug">
        <strong className="font-semibold">Clinical review required.</strong> This is AI-assisted,
        advisory content. HospiceGuard does not make autonomous clinical, billing, or eligibility
        decisions — a qualified professional must review and approve before use.
      </span>
    </div>
  );
};

export default ClinicalReviewBanner;
